import { observer } from 'mobx-react';
import ProfileLayout from '../Profile/components/ProfileLayout';
import { useStore } from '../../core/utils/hook';
import { canShowTrainingListMenu } from '../../core/utils/gaMenuPermissions';
import { useCallback, useEffect, useState } from 'react';
import { DATE_YMD, STUDENT_STATUS, SYSTEM_PATH, TRAINING_COURSE_SETTINGS_STATUS, TRAINING_COURSE_SETTINGS_TYPE } from '../../core/configs/constants';
import { convertVideoDuration, formatDecimal } from '../../core/utils/common';
import Pagination from '../Courses/components/Pagination';
import SettingTrainingCourseModal from './SettingTrainingCourseModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import moment from 'moment';

import './styles.scss';

const TAB_COURSE_LIST = 'course_list';
const TAB_TICKET_PLAN = 'ticket_plan';

const CompanyTrainingListScreen = observer(() => {
    const navigate = useNavigate();

    // store
    const {
        courseStore: {
            lstTrainingCourseForGA, getTrainingCourseListForGA, pagingTrainingCourseForGA, clearPagingTrainingCourseForGA,
            lstTicketPlanForGA, getTicketPlanListForGA, pagingTicketPlanForGA, clearPagingTicketPlanForGA
        },
        userStore: { userInfoHeader, getUserInfoHeader }
    } = useStore();

    // other
    const [searchParams, setSearchParams] = useSearchParams();

    // state
    const [activeTab, setActiveTab] = useState(
        searchParams.get('tab') === TAB_TICKET_PLAN ? TAB_TICKET_PLAN : TAB_COURSE_LIST
    );
    const [courseName, setCourseName] = useState('');
    const [ticketPlanName, setTicketPlanName] = useState('');

    // lifecycle
    useEffect(() => {
        if (!userInfoHeader?.id) {
            getUserInfoHeader();
            return;
        }
        if (!canShowTrainingListMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
            return;
        }
        fetchCourseData();
        if (activeTab === TAB_TICKET_PLAN) {
            fetchTicketPlanData();
        }
        return () => {
            clearPagingTrainingCourseForGA();
            clearPagingTicketPlanForGA();
        }
    }, [userInfoHeader?.id, userInfoHeader?.isAllowTrainingList, activeTab]);

    // function
    const fetchCourseData = (name, hideLoading = false) => {
        getTrainingCourseListForGA({ courseName: name }, hideLoading);
    }

    const fetchTicketPlanData = (name, hideLoading = false) => {
        getTicketPlanListForGA({ name }, hideLoading);
    }

    const onFetchCourseData = (data) => {
        getTrainingCourseListForGA(data);
    }

    const onFetchTicketPlanData = (data) => {
        getTicketPlanListForGA({ name: ticketPlanName, ...data });
    }

    const onDebounceCourseSearch = useCallback(
        debounce((search) => {
            fetchCourseData(search, true);
        }, 500),
        []
    );

    const onDebounceTicketPlanSearch = useCallback(
        debounce((search) => {
            fetchTicketPlanData(search, true);
        }, 500),
        []
    );

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === TAB_TICKET_PLAN) {
            setSearchParams({ tab: TAB_TICKET_PLAN });
            if (lstTicketPlanForGA?.elements === undefined) {
                fetchTicketPlanData();
            }
        } else {
            setSearchParams({});
        }
    }

    return (
        <ProfileLayout titlePage='研修一覧'>
            <div className='company-training-list-screen'>
                <div className='header'>
                    <div className='header-item primary'>
                        <div className='header-item-icon-primary'>
                            <img src='/images/icon-company-training-list-hover.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>研修講座の総数</div>
                            <div className='header-item-value-content'>{lstTrainingCourseForGA?.totalTrainingCourse}</div>
                        </div>
                    </div>
                    <div className='header-item primary'>
                        <div className='header-item-icon-primary'>
                            <img src='/images/icon-company-training-list-hover.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>受講開始した受講者数</div>
                            <div className='header-item-value-content'>{lstTrainingCourseForGA?.numberOfStudentInTrainingPeriod}</div>
                        </div>
                    </div>
                    <div className='header-item second'>
                        <div className='header-item-icon-second'>
                            <img src='/images/icon-average-first-test-result.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>初回平均点（全講座）</div>
                            <div className='header-item-value-content'>
                                {typeof lstTrainingCourseForGA?.averageOfTotalFirstTestResultForAllCourse === 'number' &&
                                !isNaN(lstTrainingCourseForGA?.averageOfTotalFirstTestResultForAllCourse) ?
                                    `${formatDecimal(lstTrainingCourseForGA.averageOfTotalFirstTestResultForAllCourse)}%` : '--'}</div>
                        </div>
                    </div>
                    <div className='header-item second'>
                        <div className='header-item-icon-second'>
                            <img src='/images/icon-total-report-submit.svg' alt='' />
                        </div>
                        <div className='header-item-value'>
                            <div className='header-item-value-title'>提出済みレポート数</div>
                            <div className='header-item-value-content'>{lstTrainingCourseForGA?.totalSubmitReportForAllTrainingCourse}</div>
                        </div>
                    </div>
                </div>
                <div className='content'>
                    <div className='content-title'>
                        <div className='title'>研修一覧</div>
                        {activeTab === TAB_COURSE_LIST && (
                            <div className='input-search-container'>
                                <input className='input-search' onChange={(e) => {
                                    setCourseName(e.target.value);
                                    onDebounceCourseSearch(e.target.value);
                                }} value={courseName} placeholder='講座名で検索'/>
                                <img className='icon-search' src='/images/icon-search.svg' alt='' />
                            </div>
                        )}
                        {activeTab === TAB_TICKET_PLAN && (
                            <div className='input-search-container'>
                                <input className='input-search' onChange={(e) => {
                                    setTicketPlanName(e.target.value);
                                    onDebounceTicketPlanSearch(e.target.value);
                                }} value={ticketPlanName} placeholder='受講プラン名で検索'/>
                                <img className='icon-search' src='/images/icon-search.svg' alt='' />
                            </div>
                        )}
                    </div>
                    <div className={`training-tabs${activeTab === TAB_TICKET_PLAN ? ' training-tabs--thin' : ''}`}>
                        <button
                            className={`training-tab-btn${activeTab === TAB_COURSE_LIST ? ' active' : ''}`}
                            onClick={() => handleTabChange(TAB_COURSE_LIST)}
                        >
                            講座一覧
                        </button>
                        <button
                            className={`training-tab-btn${activeTab === TAB_TICKET_PLAN ? ' active' : ''}`}
                            onClick={() => handleTabChange(TAB_TICKET_PLAN)}
                        >
                            受講プラン
                        </button>
                    </div>
                    {activeTab === TAB_COURSE_LIST && (
                        <div className='content-list'>
                            {
                                lstTrainingCourseForGA?.elements?.length > 0 ?
                                    lstTrainingCourseForGA.elements.map((course, index) => (
                                        <CompanyTrainingCourseItem
                                            key={index}
                                            id={course.id}
                                            thumbnail={course.thumbnail}
                                            name={course.name}
                                            totalLecture={course.totalLecture}
                                            totalTimeCourse={course.totalTimeCourse}
                                            totalStudentAttending={course.totalStudentAttending}
                                            totalStudentAttended={course.totalStudentAttended}
                                            averageFirstTestResult={course.averageFirstTestResult}
                                            submitReportStatus={course.submitReportStatus}
                                            submitQuestionStatus={course.submitQuestionStatus}
                                            numberOfReport={course.numberOfReport}
                                            numberOfQuestion={course.numberOfQuestion}
                                            submitReportType={course.submitReportType}
                                            submitReportDuration={course.submitReportDuration}
                                            reportMaxLength={course.reportMaxLength}
                                            submitQuestionType={course.submitQuestionType}
                                            submitQuestionDuration={course.submitQuestionDuration}
                                            incentivePeriod={course.incentivePeriod}
                                            fetchData={fetchCourseData}
                                        />
                                    )) : <div className='d-flex align-items-center justify-content-center min-height-200'>データはありません</div>
                            }
                            {lstTrainingCourseForGA?.elements?.length > 0 &&
                                <Pagination paging={pagingTrainingCourseForGA} onFetch={onFetchCourseData} />}
                        </div>
                    )}
                    {activeTab === TAB_TICKET_PLAN && (
                        <div className='content-list ticket-plan-list'>
                            {lstTicketPlanForGA?.elements?.length > 0 ? (
                                <>
                                    {lstTicketPlanForGA.elements.map((plan) => (
                                        <TicketPlanItem key={plan.id} plan={plan} />
                                    ))}
                                    <Pagination paging={pagingTicketPlanForGA} onFetch={onFetchTicketPlanData} />
                                </>
                            ) : (
                                <div className='d-flex align-items-center justify-content-center min-height-200'>データはありません</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ProfileLayout>
    )
});

const TicketPlanItem = ({ plan }) => {
    const navigate = useNavigate();
    const {
        groupAdminStore: { getStudentIdsByTicket },
        apiStore: { showLoading, hideLoading },
        modalStore: { openAlert }
    } = useStore();

    const handleShowCompletionSetting = () => {
        navigate(
            SYSTEM_PATH.COMPANY_TRAINING_COMPLETION_SETTINGS.replace(':ticketId', plan.id),
            { state: { ticketPlanName: plan.name } }
        );
    };

    // Validation order per spec: (1) ticket plan must have at least 1 certificate-eligible
    // course, (2) it must have at least 1 active student — checked here, before navigating,
    // so the download screen is only ever reached once both conditions already hold.
    const handleShowCertificateDownload = async () => {
        if (!plan.completionTarget) {
            openAlert('受講プランに修了証明書発行対象の講座が含まれていないため、この操作は実行できません。');
            return;
        }

        showLoading();
        const userIds = await getStudentIdsByTicket(plan.id, { status: STUDENT_STATUS.ACTIVE });
        hideLoading();

        if (!userIds.length) {
            openAlert('受講プランを利用している受講者がいないため、この操作は実行できません。');
            return;
        }

        navigate(
            SYSTEM_PATH.COMPANY_TRAINING_CERTIFICATE_DOWNLOAD.replace(':ticketId', plan.id),
            { state: { ticketPlanName: plan.name } }
        );
    };

    return (
        <div className='ticket-plan-item'>
            <div className='ticket-plan-col'>
                <div className='ticket-plan-label'>受講プラン</div>
                <div className='ticket-plan-value'>{plan.name}</div>
            </div>
            <div className='ticket-plan-col'>
                <div className='ticket-plan-label'>指定講座数</div>
                <div className='ticket-plan-value'>{plan.totalCourse ?? '--'}</div>
            </div>
            <div className='ticket-plan-col'>
                <div className='ticket-plan-label'>修了証名</div>
                <div className='ticket-plan-value'>{plan.completionCertificateName ?? '--'}</div>
            </div>
            <div className='ticket-plan-col'>
                <div className='ticket-plan-label'>修了証対象</div>
                <div className='ticket-plan-value'>{plan.completionTarget ?? 0}</div>
            </div>
            <div className='ticket-plan-col ticket-plan-action'>
                <button
                    className='completion-setting-btn'
                    type='button'
                    onClick={handleShowCompletionSetting}
                    title='修了証設定'
                    aria-label='修了証設定'
                >
                    <img src='/images/icon-settings-2.svg' alt='修了証設定' />
                </button>
                <button
                    className='certificate-download-btn'
                    type='button'
                    onClick={handleShowCertificateDownload}
                    title='修了証ダウンロード'
                    aria-label='修了証ダウンロード'
                >
                    <img src='/images/icon-pdf.svg' alt='修了証ダウンロード' />
                </button>
            </div>
        </div>
    );
};

const CompanyTrainingCourseItem = ({
    id,
    thumbnail,
    name,
    totalLecture,
    totalTimeCourse,
    totalStudentAttending,
    totalStudentAttended,
    averageFirstTestResult,
    submitReportStatus,
    submitQuestionStatus,
    numberOfReport,
    numberOfQuestion,
    submitReportType,
    submitReportDuration,
    reportMaxLength,
    submitQuestionType,
    submitQuestionDuration,
    incentivePeriod,
    fetchData
}) => {
    // other
    const navigate = useNavigate();

    // store
    const { modalStore: { show, hide } } = useStore();

    // function
    const handleShowPopupSetting = () => {
        show({
            id: 'modal-setting-training-course',
            isOpen: true,
            header: '研修設定',
            onCancel: () => hide(),
            children: (
                <SettingTrainingCourseModal data={{
                    submitReportStatus,
                    submitReportType,
                    submitReportDuration,
                    submitQuestionStatus,
                    submitQuestionType,
                    submitQuestionDuration,
                    incentivePeriod,
                    reportMaxLength
                }} fetchData={fetchData} id={id} />
            ),
            type: 'medium min-width-750'
        })
    }

    return (
        <div className='company-training-course-item'>
            <div className='thumbnail-container'>

            </div>
            <div className='content'>
                <div className='content-header'>
                    <div className='content-header-thumbnail'>
                        <img className='thumbnail' src={thumbnail || '/images/img-1.png'} alt=''></img>
                    </div>
                    <div className='content-header-info'>
                        <div className='content-header-name line-clamp-2'>{name}</div>
                        <div className='content-header-desc'>
                            <div className='total-lecture d-flex align-items-center flex-gap-5'>
                                <img src='/images/icon2.svg' className='mg-0' alt=''
                                    style={{ width: '14px', height: '14px' }} />
                                {totalLecture ?? 0} コンテンツ
                            </div>
                            <div className='total-time-course d-flex align-items-center flex-gap-5'>
                                <img className='mg-0' src='/images/icon1.svg' alt=''
                                    style={{ width: '14px', height: '14px' }}/>
                                <span>{convertVideoDuration(totalTimeCourse)} 分</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='content-detail'>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>
                            受講した受講者数
                            <img
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                                title={'この講座に指定された総受講者数に対する、受講した受講者数'}
                                style={{ height: '13px', width: '13px' }}
                                src='/images/icon-report-sub.svg'
                                className='mg-l-5'
                                alt=''/>
                        </div>
                        <div className='content-detail-item-value'>{`${totalStudentAttending}/${totalStudentAttended}`}</div>
                    </div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>
                            受講推奨期限
                        </div>
                        <div className='content-detail-item-value'>{incentivePeriod ? moment(incentivePeriod).format(DATE_YMD) : '--'}</div>
                    </div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>初回平均点</div>
                        <div className='content-detail-item-value'>
                            {typeof averageFirstTestResult === 'number' && !isNaN(averageFirstTestResult) ?
                                `${formatDecimal(averageFirstTestResult)}%` : '--'}
                        </div>
                    </div>
                    <div className='content-detail-item item-temp'></div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>
                            <span>レポート</span>
                            {
                                submitReportStatus === TRAINING_COURSE_SETTINGS_STATUS.ON && (
                                    <img
                                        style={{ height: '13px', width: '13px' }}
                                        src='/images/icon-report-sub.svg'
                                        className='mg-l-5'
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="bottom"
                                        // eslint-disable-next-line max-len
                                        title={`提出期限: ${submitReportDuration}${submitReportType === TRAINING_COURSE_SETTINGS_TYPE.MONTH ? 'ヶ月' : '日'}（受講完了日から）`}
                                        alt=''/>
                                )
                            }
                        </div>
                        <div className='content-detail-item-value status-submit'>
                            {submitReportStatus === TRAINING_COURSE_SETTINGS_STATUS.ON ? 'ON' : 'OFF'}</div>
                    </div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>
                            <span>質問</span>
                            {
                                submitQuestionStatus === TRAINING_COURSE_SETTINGS_STATUS.ON && (
                                    <img
                                        style={{ height: '13px', width: '13px' }}
                                        src='/images/icon-report-sub.svg'
                                        className='mg-l-5'
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="bottom"
                                        // eslint-disable-next-line max-len
                                        title={`提出期限: ${submitQuestionDuration}${submitQuestionType === TRAINING_COURSE_SETTINGS_TYPE.MONTH ? 'ヶ月' : '日'}（受講完了日から）`}
                                        alt=''/>
                                )
                            }
                        </div>
                        <div className='content-detail-item-value status-submit'>
                            {submitQuestionStatus === TRAINING_COURSE_SETTINGS_STATUS.ON ? 'ON' : 'OFF'}</div>
                    </div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>提出されたレポート数</div>
                        <div className='content-detail-item-value'>{numberOfReport || '--'}</div>
                    </div>
                    <div className='content-detail-item'>
                        <div className='content-detail-item-header'>質問数</div>
                        <div className='content-detail-item-value'>{numberOfQuestion || '--'}</div>
                    </div>
                </div>
            </div>
            <div className='footer-button'>
                <button className='training-course-info-btn' type='button' onClick={() => navigate(`/company-training-course/${id}`)}>
                    <img src='/images/icon-info-trainingcourse.svg' alt='' />
                    <span>受講一覧を見る</span>
                </button>
                <button className='training-course-setting-btn' type='button' onClick={() => handleShowPopupSetting()}>
                    <img src='/images/icon-settings-2.svg' alt='' />
                    <span>講座設定</span>
                </button>
            </div>
        </div>
    )
}

export default CompanyTrainingListScreen;
